import React, { useEffect } from 'react';
import Layout from '../components/layout/Layout';
import HeroSection from '../components/shared/HeroSection';
import SectionTitle from '../components/shared/SectionTitle';
import CTASection from '../components/shared/CTASection';
import SEOHead from '../components/shared/SEOHead';
import { ParallaxSection, ParallaxImage, ParallaxElement, FadeInOnScroll } from '../components/shared/Parallax';
import { heroContent, pergotendeBenefits } from '../data/mock';
import { pageSEO, generateServiceSchema } from '../utils/seo';
import { Check, Home, Droplets, Wind, Thermometer, Sun, Moon, PanelTop, Shield, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const PergotendePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: Home,
      title: "Amplia lo Spazio Abitabile",
      description: "Trasforma terrazzi e giardini in vere e proprie stanze all'aperto, utilizzabili 365 giorni l'anno."
    },
    {
      icon: Droplets,
      title: "Protezione dalla Pioggia",
      description: "Teli impermeabili e sistemi di deflusso integrati per un comfort garantito anche nelle giornate piovose."
    },
    {
      icon: Wind,
      title: "Resistenza al Vento",
      description: "Strutture certificate per resistere a raffiche importanti, con optional di chiusure laterali."
    },
    {
      icon: Thermometer,
      title: "Comfort Termico",
      description: "Tessuti tecnici che filtrano il calore e mantengono una temperatura gradevole sotto la copertura."
    }
  ];

  return (
    <Layout>
      <SEOHead
        title={pageSEO.pergotende.title}
        description={pageSEO.pergotende.description}
        keywords={pageSEO.pergotende.keywords}
        schema={generateServiceSchema({
          name: "Pergotende",
          description: "Pergotende su misura a Pisa e Toscana per esterni residenziali e commerciali"
        })}
      />

      {/* Hero Section */}
      <HeroSection
        title="Pergotende Premium a Pisa e Provincia"
        subtitle="Strutture eleganti per vivere terrazzi e giardini tutto l'anno. Protezione da sole, pioggia e vento."
        ctaText="Richiedi una consulenza dedicata"
        ctaLink="/preventivo"
        videoUrl={heroContent.pergotende.videoUrl}
        imageUrl={heroContent.pergotende.imageUrl}
        showScrollIndicator={true}
      />

      {/* Intro Section */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeInOnScroll>
              <div>
                <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                  Pergotende Pisa
                </span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-gray-900 leading-tight mb-6">
                  L'outdoor living tutto l'anno in Toscana
                </h2>
                <p className="text-lg text-gray-600 font-light leading-relaxed mb-6">
                  Le <strong>pergotende</strong> rappresentano l'evoluzione della tradizionale tenda da sole. 
                  A Pisa e in tutta la Toscana, queste strutture autoportanti o addossate creano 
                  ambienti coperti eleganti e funzionali, perfetti per vivere l'esterno 
                  senza dipendere dalle condizioni meteo.
                </p>
                <p className="text-lg text-gray-600 font-light leading-relaxed">
                  Ideali per terrazzi di ville, giardini, ristoranti e hotel della costa toscana, 
                  le nostre pergotende combinano design italiano, materiali premium e tecnologia 
                  avanzata per offrire il massimo del comfort e della durabilità.
                </p>
              </div>
            </FadeInOnScroll>
            <FadeInOnScroll delay={200}>
              <ParallaxImage
                src="https://customer-assets.emergentagent.com/job_tuscany-tende/artifacts/x9gkujl1_IMG_6406.jpeg"
                alt="Pergotenda elegante"
                speed={0.15}
                aspectRatio="4/5"
              />
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* 5% Slope Advantage Section */}
      <section className="py-24 lg:py-32 bg-gradient-to-br from-sky-50 to-blue-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeInOnScroll delay={200} className="order-2 lg:order-1">
              <div className="relative overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_828fb547-63e8-4146-aaa2-2aed84a5e0d5/artifacts/jhcv5yy1_PERGOTENDA.png"
                  alt="Pergotenda con pendenza minima del 5%"
                  className="w-full h-auto object-cover rounded-lg shadow-xl"
                />
              </div>
            </FadeInOnScroll>
            <FadeInOnScroll className="order-1 lg:order-2">
              <div>
                <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                  Il Nostro Vantaggio Esclusivo
                </span>
                <h2 className="text-3xl md:text-4xl font-light text-gray-900 leading-tight mb-6">
                  Pendenza minima, design massimo
                </h2>
                
                {/* Big 5% highlight */}
                <div className="flex items-center gap-6 mb-8 p-6 bg-white rounded-lg shadow-lg border-l-4 border-sky-500">
                  <div className="text-center">
                    <div className="text-6xl md:text-7xl font-bold text-sky-500">5%</div>
                    <div className="text-sm text-gray-500 uppercase tracking-wider">pendenza</div>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-700 font-medium mb-1">Le nostre pergotende</p>
                    <p className="text-gray-500 text-sm">
                      contro il <span className="font-semibold text-gray-700">15%</span> della concorrenza
                    </p>
                  </div>
                </div>

                <p className="text-lg text-gray-600 font-light leading-relaxed mb-6">
                  A differenza dei concorrenti, le nostre pergotende possono essere installate con 
                  una pendenza di soli <strong>5%</strong>, rispetto al 15% richiesto dai sistemi tradizionali.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full bg-sky-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div>
                      <span className="text-gray-900 font-medium">Ideale per spazi con altezza limitata</span>
                      <p className="text-gray-500 text-sm mt-1">Perfetta anche dove altre pergotende non possono essere installate</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full bg-sky-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div>
                      <span className="text-gray-900 font-medium">Design più elegante e lineare</span>
                      <p className="text-gray-500 text-sm mt-1">Linee pulite che si integrano con qualsiasi architettura</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full bg-sky-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div>
                      <span className="text-gray-900 font-medium">Maggiore spazio vivibile</span>
                      <p className="text-gray-500 text-sm mt-1">Più altezza libera sotto la copertura per il massimo comfort</p>
                    </div>
                  </div>
                </div>
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 lg:py-32 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <SectionTitle
              label="Caratteristiche"
              title="Un sistema pensato per il tuo comfort"
            />
          </FadeInOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <FadeInOnScroll key={index} delay={index * 100}>
                <div 
                  className="p-8 lg:p-10 bg-white border border-gray-100 hover:border-sky-200 transition-all duration-500 hover:shadow-xl group flex gap-6"
                >
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 rounded-full bg-sky-500/10 flex items-center justify-center group-hover:bg-sky-500 transition-colors duration-300">
                      <feature.icon className="w-6 h-6 text-sky-500 group-hover:text-white transition-colors duration-300" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-medium text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 font-light leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </FadeInOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeInOnScroll className="order-2 lg:order-1">
              <ParallaxImage
                src="https://customer-assets.emergentagent.com/job_tuscany-tende/artifacts/xs1c64dj_hf_20260218_082136_4546cfd5-dbc1-4846-9647-c3157938a14b.png"
                alt="Pergotenda in giardino"
                speed={0.15}
                aspectRatio="4/3"
              />
            </FadeInOnScroll>
            <FadeInOnScroll delay={200} className="order-1 lg:order-2">
              <div>
                <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                  I Vantaggi
                </span>
                <h2 className="text-3xl md:text-4xl font-light text-gray-900 leading-tight mb-8">
                  Perché scegliere una pergotenda
                </h2>
                <ul className="space-y-4">
                  {pergotendeBenefits.map((benefit, index) => (
                    <li key={index} className="flex items-center gap-4">
                      <div className="w-6 h-6 rounded-full bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3.5 h-3.5 text-sky-500" />
                      </div>
                      <span className="text-gray-700 font-light">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* Day & Night - LED Section */}
      <section className="py-24 lg:py-32 bg-gray-900 text-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <div className="text-center mb-16">
              <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-400">
                Illuminazione Integrata
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light leading-tight mb-6">
                Vivi il tuo spazio di giorno e di sera
              </h2>
              <p className="text-lg text-gray-300 font-light max-w-3xl mx-auto">
                Le nostre pergotende possono essere dotate di illuminazione LED integrata nella struttura. 
                Una soluzione elegante e funzionale che trasforma il tuo spazio esterno in un ambiente 
                accogliente anche dopo il tramonto.
              </p>
            </div>
          </FadeInOnScroll>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Day Image */}
            <FadeInOnScroll delay={100}>
              <div className="relative overflow-hidden group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_828fb547-63e8-4146-aaa2-2aed84a5e0d5/artifacts/8fdt6leg_PERGOTENDA.png"
                  alt="Pergotenda di giorno"
                  className="w-full h-[400px] object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent">
                  <div className="flex items-center gap-3">
                    <Sun className="w-6 h-6 text-yellow-400" />
                    <div>
                      <h4 className="text-lg font-medium text-white">Di giorno</h4>
                      <p className="text-sm text-white/70">Protezione solare ottimale</p>
                    </div>
                  </div>
                </div>
              </div>
            </FadeInOnScroll>
            
            {/* Night Image */}
            <FadeInOnScroll delay={200}>
              <div className="relative overflow-hidden group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_828fb547-63e8-4146-aaa2-2aed84a5e0d5/artifacts/avc8ciog_PERGOTENDA%20NOTTE.jpeg"
                  alt="Pergotenda di notte con illuminazione LED"
                  className="w-full h-[400px] object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent">
                  <div className="flex items-center gap-3">
                    <Moon className="w-6 h-6 text-sky-400" />
                    <div>
                      <h4 className="text-lg font-medium text-white">Di sera</h4>
                      <p className="text-sm text-white/70">Illuminazione LED integrata</p>
                    </div>
                  </div>
                </div>
              </div>
            </FadeInOnScroll>
          </div>

          <FadeInOnScroll delay={300}>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div className="p-6">
                <h4 className="text-lg font-medium text-white mb-2">Luce calda e soffusa</h4>
                <p className="text-gray-400 font-light text-sm">Atmosfera accogliente per cene e momenti di relax</p>
              </div>
              <div className="p-6">
                <h4 className="text-lg font-medium text-white mb-2">Basso consumo energetico</h4>
                <p className="text-gray-400 font-light text-sm">Tecnologia LED ad alta efficienza</p>
              </div>
              <div className="p-6">
                <h4 className="text-lg font-medium text-white mb-2">Controllo da remoto</h4>
                <p className="text-gray-400 font-light text-sm">Gestione tramite telecomando o smartphone</p>
              </div>
            </div>
          </FadeInOnScroll>
        </div>
      </section>

      {/* Glass Enclosure Section */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeInOnScroll>
              <div>
                <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                  Chiusure Vetrate
                </span>
                <h2 className="text-3xl md:text-4xl font-light text-gray-900 leading-tight mb-6">
                  Completa la tua pergotenda con le vetrate
                </h2>
                <p className="text-lg text-gray-600 font-light leading-relaxed mb-6">
                  Aggiungi alla tua pergotenda <strong>chiusure perimetrali in vetro</strong> per creare 
                  un vero e proprio ambiente chiuso, vivibile in ogni stagione dell'anno. 
                  Le vetrate scorrevoli permettono di aprire completamente lo spazio nelle 
                  giornate più belle e di proteggersi da vento, pioggia e freddo quando necessario.
                </p>
                <ul className="space-y-4">
                  <li className="flex items-center gap-4">
                    <div className="w-6 h-6 rounded-full bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3.5 h-3.5 text-sky-500" />
                    </div>
                    <span className="text-gray-700 font-light">Vetri temperati di sicurezza</span>
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-6 h-6 rounded-full bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3.5 h-3.5 text-sky-500" />
                    </div>
                    <span className="text-gray-700 font-light">Sistema scorrevole impacchettabile</span>
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-6 h-6 rounded-full bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3.5 h-3.5 text-sky-500" />
                    </div>
                    <span className="text-gray-700 font-light">Isolamento termico e acustico</span>
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-6 h-6 rounded-full bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3.5 h-3.5 text-sky-500" />
                    </div>
                    <span className="text-gray-700 font-light">Vivibilità 365 giorni l'anno</span>
                  </li>
                </ul>
              </div>
            </FadeInOnScroll>
            <FadeInOnScroll delay={200}>
              <div className="relative overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_828fb547-63e8-4146-aaa2-2aed84a5e0d5/artifacts/oplipuz4_PERGOLA%20CON%20VETRI.jpeg"
                  alt="Pergotenda con chiusure in vetro"
                  className="w-full h-auto object-cover"
                />
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* Parallax Gallery Section */}
      <ParallaxSection
        imageUrl="https://customer-assets.emergentagent.com/job_tuscany-tende/artifacts/npo3zslt_progetto%20pergotenda.png"
        speed={0.3}
        overlayOpacity={0.5}
        minHeight="60vh"
      >
        <div className="h-[60vh] flex items-center justify-center">
          <div className="text-center px-6">
            <ParallaxElement speed={0.15} direction="up">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-4">
                Progetti su misura
              </h2>
              <p className="text-lg text-white/80 font-light max-w-2xl mx-auto">
                Ogni installazione è unica, progettata per integrarsi perfettamente con l'architettura esistente.
              </p>
            </ParallaxElement>
          </div>
        </div>
      </ParallaxSection>

      {/* Financing Banner */}
      <section className="py-20 lg:py-28 bg-gradient-to-r from-sky-500 to-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '30px 30px'
          }} />
        </div>
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <FadeInOnScroll>
              <div className="text-white">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full mb-6">
                  <span className="text-sm font-medium">Finanziamento Disponibile</span>
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-light mb-6 leading-tight">
                  La tua pergotenda a<br />
                  <span className="font-semibold">Tasso Zero</span>
                </h2>
                <p className="text-xl text-white/90 font-light mb-6 leading-relaxed">
                  Paga il 50% all'ordine e il resto in 10 comode rate annuali senza interessi. 
                  TAN 0% e TAEG 0%.
                </p>
                <div className="flex items-center gap-6 mb-8">
                  <div className="text-center">
                    <div className="text-4xl font-bold">0%</div>
                    <div className="text-sm text-white/70">Interessi</div>
                  </div>
                  <div className="w-px h-12 bg-white/30" />
                  <div className="text-center">
                    <div className="text-4xl font-bold">10</div>
                    <div className="text-sm text-white/70">Rate annuali</div>
                  </div>
                  <div className="w-px h-12 bg-white/30" />
                  <div className="text-center">
                    <div className="text-4xl font-bold">50%</div>
                    <div className="text-sm text-white/70">Acconto</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white/10 rounded-xl mb-6">
                  <Shield className="w-8 h-8 text-white" />
                  <div>
                    <p className="font-medium">Alla documentazione ci pensiamo noi</p>
                    <p className="text-sm text-white/70">Gestiamo tutta la pratica per te</p>
                  </div>
                </div>
                <Link to="/giovanni-tende-smart">
                  <button className="bg-white text-sky-600 hover:bg-gray-100 px-8 py-4 font-medium transition-colors inline-flex items-center gap-2">
                    Scopri il finanziamento
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </Link>
              </div>
            </FadeInOnScroll>
            
            <FadeInOnScroll delay={200}>
              <div className="bg-white rounded-2xl p-8 shadow-2xl">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">Esempio: Pergotenda da €8.000</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">Acconto (50%)</span>
                    <span className="text-xl font-semibold">€ 4.000</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">Importo finanziato</span>
                    <span className="text-xl font-semibold text-sky-500">€ 4.000</span>
                  </div>
                  <div className="bg-sky-50 -mx-8 px-8 py-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-gray-900 font-medium">Rata annuale</span>
                        <span className="text-gray-500 text-sm block">per 10 anni</span>
                      </div>
                      <span className="text-3xl font-bold text-sky-500">€ 400</span>
                    </div>
                  </div>
                </div>
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection
        title="Pronto a trasformare il tuo spazio?"
        description="Richiedi una consulenza gratuita e scopri la pergotenda perfetta per te."
        variant="light"
      />
    </Layout>
  );
};

export default PergotendePage;
